import * as React from 'react';
import { Box, Grid } from '@mui/material';
import {
	CerebroComplexListItem,
	CerebroDrawer,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
	CerebroStyledBox,
	CerebroForm,
} from '.';
import { ReactComponent as WorkingCapital } from '../icons/WorkingCapital.svg';
import { DealRequestAllocation } from '../models';
import { Field, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../context';
import { useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { getApiResponseData } from '../utils/axios';
import { BooleanUtils, HtmlUtils, NumberUtils } from '../utils';
import { DebtCapitalSectionProps } from '../../pages';
import { CerebroApiEndpoints } from '../utils/api';
import { QueryKeys } from '../hooks';
import { DebtCapitalHeading } from './DebtCapitalHeading';
import { MaxCurrencyLength } from '../utils/platform';

export const CerebroDebtWorkingCapital: React.FC<DebtCapitalSectionProps> = ({
	allocation,
	drawerOpenByDefault = false,
}) => {
	const [open, setOpen] = React.useState(drawerOpenByDefault);
	const api = useApi();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();
	const snackbar = useSnackbar();

	const isEmpty = isWorkingCapitalEmpty(allocation);

	const validateForm = (values: DealRequestAllocationWorkingCapitalValues) => {
		const errors: DealRequestAllocationWorkingCapitalValueErrors = {};

		if (!values.totalWorkingCapitalAmount) {
			errors.totalWorkingCapitalAmount = 'Amount is required!';
		}

		if (
			!values.workingCapitalImproveLiquidity &&
			!values.workingCapitalPurchaseInventory &&
			!values.workingCapitalOperatingExpenses
		) {
			errors.workingCapitalImproveLiquidity = 'At least one working capital purpose must be selected.';
		}

		if (allocation.acquiringBusiness == true) {
			if (values.workingCapitalForAcquisition == true && !values.WorkingCapitalForAcquisitionAmount) {
				errors.WorkingCapitalForAcquisitionAmount = 'Amount is required!';
			}
		}

		return errors;
	};

	const { mutate } = useMutation(
		async (values: DealRequestAllocationWorkingCapitalValues) => {
			const payload: Partial<DealRequestAllocation> = {
				totalWorkingCapitalAmount: NumberUtils.ParseDecimal(values.totalWorkingCapitalAmount),
				workingCapitalImproveLiquidity: values.workingCapitalImproveLiquidity,
				workingCapitalPurchaseInventory: values.workingCapitalPurchaseInventory,
				workingCapitalOperatingExpenses: values.workingCapitalOperatingExpenses,
				workingCapitalForAcquisition: values.workingCapitalForAcquisition,
				workingCapitalForAcquisitionAmount: NumberUtils.ParseDecimal(values.WorkingCapitalForAcquisitionAmount),
			};

			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), payload)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				setOpen(false);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	const drawerRef = React.useRef<HTMLDivElement>(null);

	return (
		<>
			<DebtCapitalHeading icon={WorkingCapital} amountRequested={allocation.totalWorkingCapitalAmount ?? 0}>
				Working Capital
			</DebtCapitalHeading>

			{isEmpty ? (
				<CerebroStyledBox sx={{ marginBottom: 8 }}>
					<CerebroHeading size="h2" component="h2">
						No details have been added yet.
					</CerebroHeading>
					<br />
					<br />
					<CerebroPrimaryButton wide onClick={() => setOpen(true)}>
						Add Working Capital Details
					</CerebroPrimaryButton>
					<br />
				</CerebroStyledBox>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }} onEdit={() => setOpen(true)}>
					<Grid container>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Do you need to finance your accounts receivable?
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.workingCapitalImproveLiquidity)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Do you need to finance inventory?
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.workingCapitalPurchaseInventory)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Do you need capital for operating expenses (SG&amp;A)?
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.workingCapitalOperatingExpenses)}
							</CerebroText>
						</Grid>
						{allocation.acquiringBusiness == true && (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Will any of this financing be applied to the purchase price of the acquisition?
								</CerebroLabel>
								<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
									{BooleanUtils.YesNo(allocation.workingCapitalForAcquisition)}
								</CerebroText>
							</Grid>
						)}
						{allocation.acquiringBusiness == true && allocation.workingCapitalForAcquisition == true && (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Amount applied to acquisition
								</CerebroLabel>
								<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
									{NumberUtils.FormatCurrency(allocation.workingCapitalForAcquisitionAmount)}
								</CerebroText>
							</Grid>
						)}
					</Grid>
				</CerebroComplexListItem>
			)}

			<CerebroDrawer ref={drawerRef} open={open} onClose={() => setOpen(false)} icon={WorkingCapital}>
				<CerebroHeading>Working Capital</CerebroHeading>
				<br />
				<Formik
					initialValues={{
						totalWorkingCapitalAmount: NumberUtils.FormatCurrency(allocation.totalWorkingCapitalAmount),
						workingCapitalImproveLiquidity: allocation.workingCapitalImproveLiquidity ?? false,
						workingCapitalPurchaseInventory: allocation.workingCapitalPurchaseInventory ?? false,
						workingCapitalOperatingExpenses: allocation.workingCapitalOperatingExpenses ?? false,
						workingCapitalForAcquisition: allocation.workingCapitalForAcquisition,
						WorkingCapitalForAcquisitionAmount: NumberUtils.FormatCurrency(
							allocation.workingCapitalForAcquisitionAmount
						),
					}}
					validate={validateForm}
					onSubmit={values => mutate(values)}
				>
					{({ isSubmitting, errors, touched, handleSubmit, values }) => {
						const purposeError =
							errors.workingCapitalImproveLiquidity !== undefined &&
							touched.workingCapitalImproveLiquidity;

						return (
							<CerebroForm
								flatIndicator
								onSubmit={handleSubmit}
								scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(drawerRef.current)}
							>
								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel
										required
										htmlFor="totalWorkingCapitalAmount"
										error={
											touched.totalWorkingCapitalAmount &&
											errors.totalWorkingCapitalAmount != null
										}
									>
										How much working capital do you need?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="totalWorkingCapitalAmount"
										name="totalWorkingCapitalAmount"
										disabled={isSubmitting}
										value={values.totalWorkingCapitalAmount}
										error={
											touched.totalWorkingCapitalAmount &&
											errors.totalWorkingCapitalAmount != null
										}
									/>
									{touched.totalWorkingCapitalAmount && errors.totalWorkingCapitalAmount != null && (
										<CerebroText variant="error">{errors.totalWorkingCapitalAmount}</CerebroText>
									)}
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="workingCapitalImproveLiquidity">
										Do you need to finance your accounts receivable?
									</CerebroLabel>
									<Field
										as={CerebroYesNo}
										name="workingCapitalImproveLiquidity"
										disabled={isSubmitting}
										error={purposeError}
									/>
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="workingCapitalPurchaseInventory">
										Do you need to finance inventory?
									</CerebroLabel>
									<Field
										as={CerebroYesNo}
										name="workingCapitalPurchaseInventory"
										disabled={isSubmitting}
										error={purposeError}
									/>
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="workingCapitalOperatingExpenses">
										Do you need capital for operating expenses (SG&amp;A)?
									</CerebroLabel>
									<Field
										as={CerebroYesNo}
										name="workingCapitalOperatingExpenses"
										disabled={isSubmitting}
										error={purposeError}
									/>
								</Box>

								{purposeError && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.workingCapitalImproveLiquidity}
									</CerebroText>
								)}

								{allocation.acquiringBusiness == true && (
									<>
										<Box sx={{ marginBottom: 2 }}>
											<CerebroLabel htmlFor="workingCapitalForAcquisition">
												Will any of this financing be applied to the purchase price of the
												acquisition?
											</CerebroLabel>
											<Field
												as={CerebroYesNo}
												name="workingCapitalForAcquisition"
												disabled={isSubmitting}
											/>
										</Box>

										{values.workingCapitalForAcquisition == true && (
											<Box sx={{ marginBottom: 2 }}>
												<CerebroLabel
													required
													htmlFor="WorkingCapitalForAcquisitionAmount"
													error={
														touched.WorkingCapitalForAcquisitionAmount &&
														errors.WorkingCapitalForAcquisitionAmount != null
													}
												>
													Amount applied to acquisition
												</CerebroLabel>
												<Field
													formatas="currency"
													as={CerebroMaskedInput}
													maxLength={MaxCurrencyLength}
													id="WorkingCapitalForAcquisitionAmount"
													name="WorkingCapitalForAcquisitionAmount"
													disabled={isSubmitting}
													value={values.WorkingCapitalForAcquisitionAmount}
													error={
														touched.WorkingCapitalForAcquisitionAmount &&
														errors.WorkingCapitalForAcquisitionAmount != null
													}
												/>
												{touched.WorkingCapitalForAcquisitionAmount &&
													errors.WorkingCapitalForAcquisitionAmount != null && (
														<CerebroText variant="error">
															{errors.WorkingCapitalForAcquisitionAmount}
														</CerebroText>
													)}
											</Box>
										)}
									</>
								)}

								<Grid container justifyContent="space-between" sx={{ marginTop: 5 }}>
									<Grid item>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Save Details
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			</CerebroDrawer>
		</>
	);
};

interface DealRequestAllocationWorkingCapitalValues {
	totalWorkingCapitalAmount?: string;
	workingCapitalImproveLiquidity?: boolean;
	workingCapitalPurchaseInventory?: boolean;
	workingCapitalOperatingExpenses?: boolean;
	workingCapitalForAcquisition?: boolean;
	WorkingCapitalForAcquisitionAmount?: string;
}

interface DealRequestAllocationWorkingCapitalValueErrors {
	totalWorkingCapitalAmount?: string;
	workingCapitalImproveLiquidity?: string;
	workingCapitalPurchaseInventory?: string;
	workingCapitalOperatingExpenses?: string;
	WorkingCapitalForAcquisitionAmount?: string;
}

export function isWorkingCapitalEmpty(allocation: DealRequestAllocation): boolean {
	return (
		!allocation.totalWorkingCapitalAmount &&
		!(allocation.workingCapitalImproveLiquidity ?? false) &&
		!(allocation.workingCapitalPurchaseInventory ?? false) &&
		!(allocation.workingCapitalOperatingExpenses ?? false) &&
		!(allocation.workingCapitalForAcquisition ?? false)
	);
}
